import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { User, UserCreate, UserUpdate } from '@/types/user';
import accountAdminUsersApi from '@/api/modules/accountAdminUsers';
import accountAdminRolesApi from '@/api/modules/accountAdminRoles';

export const useAccountAdminUserStore = defineStore('account-admin-user', () => {
  const users = ref<User[]>([]);
  const roles = ref<any[]>([]);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const fetchUsers = async () => {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await accountAdminUsersApi.getUsers();
      // Map is_admin from backend to is_account_admin for frontend
      users.value = response.map((user: any) => ({
        ...user,
        is_account_admin: user.is_admin ?? false,
      }));
    } catch (e: any) {
      error.value = e.message;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchRoles = async () => {
    try {
      roles.value = await accountAdminRolesApi.getRoles();
    } catch (e: any) {
      console.error('Failed to fetch roles:', e);
    }
  };

  const createUser = async (userData: UserCreate) => {
    isLoading.value = true;
    error.value = null;
    try {
      const newUser: any = await accountAdminUsersApi.createUser(userData);
      // Map is_admin from backend to is_account_admin for frontend
      users.value.push({
        ...newUser,
        is_account_admin: newUser.is_admin ?? false,
      });
    } catch (e: any) {
      error.value = e.message;
      throw e;
    } finally {
      isLoading.value = false;
    }
  };

  const updateUser = async (userId: string, userData: UserUpdate) => {
    isLoading.value = true;
    error.value = null;
    try {
      const updatedUser: any = await accountAdminUsersApi.updateUser(userId, userData);
      const index = users.value.findIndex((u) => u.id === userId);
      if (index !== -1) {
        // Map is_admin from backend to is_account_admin for frontend
        users.value[index] = {
          ...updatedUser,
          is_account_admin: updatedUser.is_admin ?? false,
        };
      }
    } catch (e: any) {
      error.value = e.message;
      throw e;
    } finally {
      isLoading.value = false;
    }
  };

  const deleteUser = async (userId: string) => {
    isLoading.value = true;
    error.value = null;
    try {
      await accountAdminUsersApi.deleteUser(userId);
      users.value = users.value.filter((u) => u.id !== userId);
    } catch (e: any) {
      error.value = e.message;
      throw e;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    users,
    roles,
    isLoading,
    error,
    fetchUsers,
    fetchRoles,
    createUser,
    updateUser,
    deleteUser,
  };
});
